import { assert } from '@sama/js-core';
import { Box3, Matrix4, Vector3 } from 'three';
import { TentPoleVertex } from '../point-cloud-utils';
import { roundValue } from '../point-utils';
import {
  Point,
  reportAllSubTreePoints,
  SearchBoxQuery,
  SearchConfiguration,
} from './find-closest-points';

/**
 * Points at the edge are returned as within the box.
 */
export function isPointWithinBox(boxLocalMatrix: Matrix4, point: Vector3) {
  const localPoint = point.clone().applyMatrix4(boxLocalMatrix);
  // Local point should be within the unit cube centered at the origin
  // -0.5 <= xyz <= 0.5
  return localPoint
    .toArray()
    .map(roundValue) // ignore float precision errors
    .every((component) => Math.abs(component) <= 0.5);
}

/**
 * Evaluates if tree should be further searched based on box intersection.
 *
 * @param results - modified in place if search fully contains the bounding box
 * @returns true if intersects with bounding box, false if fully contained or apart
 */
export function evaluateBoundingBox(
  results: Point[],
  boundingBox: Box3,
  index: number,
  config: SearchConfiguration,
) {
  const { searchBox } = config;
  assert(searchBox, 'should be called only when using search box');

  if (searchContainsBox(searchBox.worldToLocalMatrix, boundingBox)) {
    // Add the whole subtree to the results array
    reportAllSubTreePoints(results, index, config);
    return false;
  }

  return searchIntersectsBox(searchBox, boundingBox);
}

function searchContainsBox(searchBox: Matrix4, boundingBox: Box3) {
  return (
    isPointWithinBox(searchBox, boundingBox.min) &&
    isPointWithinBox(searchBox, boundingBox.max)
  );
}

const perpendicularAxisX = new Vector3();
const perpendicularAxisY = new Vector3();
const perpendicularAxisZ = new Vector3();
// Box3 is always axis aligned
const box3NormalAxes = [
  new Vector3(1, 0, 0),
  new Vector3(0, 1, 0),
  new Vector3(0, 0, 1),
];

/**
 * Intersection is calculated using the Separating Axes Theorem (SAT).
 */
function searchIntersectsBox(searchBox: SearchBoxQuery, boundingBox: Box3) {
  // early exit with axis aligned boxes test
  if (!boundingBox.intersectsBox(searchBox.boundingBox)) {
    return false;
  } else if (boundingBox.containsBox(searchBox.boundingBox)) {
    return true;
  }

  const normalAxes = searchBox.normalAxes.map((axis) => axis.normalize());

  const testAxes = [
    ...normalAxes,
    ...box3NormalAxes,
    ...normalAxes.flatMap((axis) => [
      perpendicularAxisX.crossVectors(axis, box3NormalAxes[0]),
      perpendicularAxisY.crossVectors(axis, box3NormalAxes[1]),
      perpendicularAxisZ.crossVectors(axis, box3NormalAxes[2]),
    ]),
  ];

  const box3Points = getBox3Points(boundingBox);
  return testAxes.every((axis) =>
    overlapsOnAxis(axis, searchBox.orientedBoxVertices, box3Points),
  );
}

/**
 * Touching boxes are considered to be overlapping.
 */
function overlapsOnAxis(
  axis: Vector3,
  firstBoxVertices: Vector3[],
  secondBoxVertices: Vector3[],
) {
  let firstBoxMinimum = Infinity;
  let firstBoxMaximum = -Infinity;
  // Project all vertices of both boxes onto the axis
  for (const vertex of firstBoxVertices) {
    const projection = vertex.dot(axis);
    firstBoxMinimum = Math.min(firstBoxMinimum, projection);
    firstBoxMaximum = Math.max(firstBoxMaximum, projection);
  }

  let secondBoxMinimum = Infinity;
  let secondBoxMaximum = -Infinity;
  for (const vertex of secondBoxVertices) {
    const projection = vertex.dot(axis);
    secondBoxMinimum = Math.min(secondBoxMinimum, projection);
    secondBoxMaximum = Math.max(secondBoxMaximum, projection);
  }

  // Check if the boxes overlap or touch on the axis
  return (
    firstBoxMaximum >= secondBoxMinimum && secondBoxMaximum >= firstBoxMinimum
  );
}

const points = Array.from({ length: 8 }, () => new Vector3());

function getBox3Points(box: Box3) {
  points[TentPoleVertex.FrontBottomRight].set(box.max.x, box.min.y, box.min.z);
  points[TentPoleVertex.FrontTopRight].set(box.max.x, box.min.y, box.max.z);
  points[TentPoleVertex.FrontBottomLeft].set(box.min.x, box.min.y, box.min.z);
  points[TentPoleVertex.FrontTopLeft].set(box.min.x, box.min.y, box.max.z);
  points[TentPoleVertex.BackBottomRight].set(box.max.x, box.max.y, box.min.z);
  points[TentPoleVertex.BackTopRight].set(box.max.x, box.max.y, box.max.z);
  points[TentPoleVertex.BackBottomLeft].set(box.min.x, box.max.y, box.min.z);
  points[TentPoleVertex.BackTopLeft].set(box.min.x, box.max.y, box.max.z);

  return points;
}
